import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { bugReporterActionLog } from '@clareefai/utils';
import { captureScreenshot, describeElement } from './captureUtils';
import { BUG_REPORTER_OPEN_EVENT } from './trigger';

interface UseBugReporterListenersOptions {
  onTrigger: (screenshot: string) => void;
}

export function useBugReporterListeners({ onTrigger }: UseBugReporterListenersOptions) {
  const location = useLocation();
  const prevPathRef = useRef(location.pathname);

  useEffect(() => {
    const trigger = async () => {
      try {
        const screenshot = await captureScreenshot();
        onTrigger(screenshot);
      } catch (err) {
        console.error('Bug reporter: failed to capture screenshot', err);
        onTrigger('');
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'B') {
        e.preventDefault();
        void trigger();
      }
    };

    const handleOpenEvent = () => void trigger();

    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener(BUG_REPORTER_OPEN_EVENT, handleOpenEvent);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener(BUG_REPORTER_OPEN_EVENT, handleOpenEvent);
    };
  }, [onTrigger]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Element | null;
      if (!target) return;
      bugReporterActionLog.push({
        type: 'click',
        detail: describeElement(target),
        timestamp: Date.now(),
      });
    };

    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, []);

  useEffect(() => {
    if (location.pathname !== prevPathRef.current) {
      bugReporterActionLog.push({
        type: 'navigation',
        detail: `${prevPathRef.current} → ${location.pathname}`,
        timestamp: Date.now(),
      });
      prevPathRef.current = location.pathname;
    }
  }, [location.pathname]);
}
